import * as React from "react"
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group"

import { cn } from "@/lib/utils"

interface OpcionDelFiltro<V extends string = string> {
  valor: V
  etiqueta: React.ReactNode
  /** La cantidad que cae en esa opción, en mono y gris medio. Sin ella no se dibuja. */
  cantidad?: number
}

/**
 * Filtro segmentado: la píldora de opciones excluyentes con contador
 * ("Todas 12 · Abiertas 3 · Respondidas"). Las pantallas con diseño lo usan para
 * filtrar una lista sin cambiar de página.
 *
 * Es un grupo de RADIOS (una sola opción elegida, flechas para moverse) y no un
 * juego de pestañas: filtrar no cambia de panel. Tocar la opción activa no la
 * deselecciona —Radix lo reporta como valor vacío y acá se ignora—, porque un
 * filtro siempre tiene una opción puesta.
 *
 * Las medidas son las del diseño (px/16): contenedor `divisor` de radio 11 con 3
 * de aire; opciones de radio 9, 11,5px/700; la activa sube a una superficie blanca
 * con sombra fina, y el contador va en 10,5px mono.
 */
function FiltroSegmentado<V extends string>({
  opciones,
  valor,
  onCambio,
  className,
  ...props
}: Omit<React.ComponentProps<typeof ToggleGroupPrimitive.Root>, "type" | "value" | "onValueChange" | "defaultValue"> & {
  opciones: OpcionDelFiltro<V>[]
  valor: V
  onCambio: (valor: V) => void
}) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="filtro-segmentado"
      type="single"
      value={valor}
      onValueChange={(siguiente) => {
        if (siguiente) onCambio(siguiente as V)
      }}
      className={cn("bg-divisor inline-flex w-fit gap-1 rounded-[0.6875rem] p-[0.1875rem]", className)}
      {...props}
    >
      {opciones.map((opcion) => (
        <ToggleGroupPrimitive.Item
          key={opcion.valor}
          value={opcion.valor}
          data-slot="filtro-segmentado-opcion"
          className="text-ink-medio data-[state=on]:bg-card data-[state=on]:text-ink focus-visible:ring-ring/50 inline-flex items-center gap-1.5 rounded-[0.5625rem] px-2.5 py-1 text-[0.71875rem] font-bold whitespace-nowrap outline-none transition-colors focus-visible:ring-[3px] data-[state=on]:shadow-[0_1px_3px_rgba(20,22,26,0.12)] disabled:pointer-events-none disabled:opacity-50"
        >
          {opcion.etiqueta}
          {opcion.cantidad !== undefined && (
            <span className="text-ink-medio font-mono text-[0.65625rem] font-bold">{opcion.cantidad}</span>
          )}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  )
}

export { FiltroSegmentado }
export type { OpcionDelFiltro }
